/**
 * LandingInquiryForm — the lead-capture form on the public landing.
 *
 * Posts to /request-quote (PR #60) via useInquiryMutation. Renders a
 * honeypot `website` field that is keyboard-skipped (tabIndex=-1) and
 * visually positioned off-screen via CSS — accessible-tree-visible
 * for bot scanners but functionally invisible to humans. The Edge
 * Function treats any non-empty value as a silent_ok.
 *
 * The form auto-tunes per vertical:
 *   - the rooms-count label changes ("# of rooms" vs "# of properties")
 *   - submitting routes the value into rooms_count or properties_count
 *     so the lead row matches the visitor's mental model.
 */

import {
  type FormEvent,
  type ReactNode,
  useState,
}                                            from 'react'
import { useInquiryMutation }                from '@/hooks/useInquiryMutation'
import {
  VERTICALS,
  VERTICAL_COPY,
  VERTICAL_LONG_LABELS,
  INQUIRY_LABEL,
}                                            from './landingCopy'
import type { Vertical, InquiryType }        from '@/types/landing'

type FormState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'submitted' }
  | { kind: 'error'; message: string }

export interface LandingInquiryFormProps {
  vertical:           Vertical
  onVerticalChange:   (v: Vertical) => void
  /** Defaults to 'quote'; the nav "Try Free" passes 'waitlist'. */
  initialInquiryType?: InquiryType
}

export function LandingInquiryForm({
  vertical,
  onVerticalChange,
  initialInquiryType = 'quote',
}: LandingInquiryFormProps): ReactNode {
  const mutation = useInquiryMutation()
  const [state, setState] = useState<FormState>({ kind: 'idle' })

  const [email,       setEmail]       = useState('')
  const [name,        setName]        = useState('')
  const [company,     setCompany]     = useState('')
  const [inquiryType, setInquiryType] = useState<InquiryType>(initialInquiryType)
  const [roomsCount,  setRoomsCount]  = useState('')
  const [message,     setMessage]     = useState('')
  const [website,     setWebsite]     = useState('')   // honeypot

  // When the parent flips initialInquiryType (e.g. nav "Try Free"
  // sets it to 'waitlist'), reflect that change into the local
  // selection. The user's manual selection still wins until the
  // parent flips again.
  //
  // Adjusted during render rather than in an effect: an effect would
  // commit the stale selection first and then immediately re-render with
  // the new one, which is a visible flash of the wrong radio on the public
  // form. Comparing against the previous prop value is React's documented
  // replacement for this exact pattern.
  const [prevInquiryType, setPrevInquiryType] = useState<InquiryType>(initialInquiryType)
  if (initialInquiryType !== prevInquiryType) {
    setPrevInquiryType(initialInquiryType)
    setInquiryType(initialInquiryType)
  }

  const copy = VERTICAL_COPY[vertical]

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault()
    setState({ kind: 'submitting' })

    const rooms = roomsCount.trim().length > 0 ? Number(roomsCount) : undefined
    const isPropertyMgmt = vertical === 'property_mgmt'

    try {
      await mutation.mutateAsync({
        email:            email.trim(),
        name:             name.trim()    || undefined,
        company:          company.trim() || undefined,
        vertical,
        inquiry_type:     inquiryType,
        message:          message.trim() || undefined,
        rooms_count:      isPropertyMgmt ? undefined : rooms,
        properties_count: isPropertyMgmt ? rooms      : undefined,
        website,
      })
      setState({ kind: 'submitted' })
    } catch (err) {
      const msg = err instanceof Error
        ? (err as Error & { userMessage?: string }).userMessage ?? err.message
        : 'Something went wrong. Please try again.'
      setState({ kind: 'error', message: msg })
    }
  }

  if (state.kind === 'submitted') {
    return (
      <section id="inquiry-form" className="landing-section landing-form-section">
        <div className="landing-section__inner landing-form-section__inner">
          <div className="landing-form-success" role="status" aria-live="polite">
            <h2>Thanks — we&apos;ll be in touch shortly.</h2>
            <p>
              Your inquiry has been recorded. A member of the team will reach
              out to <strong>{email}</strong> within one business day.
            </p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="inquiry-form" className="landing-section landing-form-section" aria-labelledby="form-h">
      <div className="landing-section__inner landing-form-section__inner">
        <h2 id="form-h">Get a quote — {VERTICAL_LONG_LABELS[vertical]}</h2>
        <p className="landing-form-section__intro">
          Fill in a few details and we&apos;ll be back to you within one business day.
          No sales calls — just a quote that fits.
        </p>

        <form className="landing-form" onSubmit={onSubmit} noValidate>
          <fieldset className="landing-form__fieldset">
            <legend className="landing-form__legend">Property type</legend>
            {VERTICALS.map((v) => (
              <label key={v} className="landing-form__radio">
                <input
                  type="radio"
                  name="vertical"
                  value={v}
                  checked={vertical === v}
                  onChange={() => onVerticalChange(v)}
                />
                {VERTICAL_LONG_LABELS[v]}
              </label>
            ))}
          </fieldset>

          <label className="landing-form__field">
            <span className="landing-form__label">
              Email <abbr title="required" aria-label="required">*</abbr>
            </span>
            <input
              type="email"
              required
              autoComplete="email"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>

          <label className="landing-form__field">
            <span className="landing-form__label">Your name</span>
            <input type="text" autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <label className="landing-form__field">
            <span className="landing-form__label">Organization</span>
            <input type="text" autoComplete="organization" value={company} onChange={(e) => setCompany(e.target.value)} />
          </label>

          <label className="landing-form__field">
            <span className="landing-form__label">{copy.roomsLabel}</span>
            <input
              type="number"
              inputMode="numeric"
              min="1"
              value={roomsCount}
              onChange={(e) => setRoomsCount(e.target.value)}
            />
          </label>

          <fieldset className="landing-form__fieldset">
            <legend className="landing-form__legend">What can we help with?</legend>
            {(['quote', 'info', 'demo', 'waitlist'] as const).map((t) => (
              <label key={t} className="landing-form__radio">
                <input
                  type="radio"
                  name="inquiry_type"
                  value={t}
                  checked={inquiryType === t}
                  onChange={() => setInquiryType(t)}
                />
                {INQUIRY_LABEL[t]}
              </label>
            ))}
          </fieldset>

          <label className="landing-form__field">
            <span className="landing-form__label">Anything else?</span>
            <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} />
          </label>

          {/* Honeypot — bots usually fill every input. CSS positions it
              off-screen while keeping it accessible-tree-visible. tabIndex
              -1 keeps keyboard users out; autocomplete=off keeps password
              managers away. The Edge Function returns silent_ok on any
              non-empty value. */}
          <label aria-hidden="true" className="landing-form__honeypot">
            Website (leave blank)
            <input
              type="text"
              tabIndex={-1}
              autoComplete="off"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
            />
          </label>

          {state.kind === 'error' && (
            <p className="landing-form__error" role="alert">{state.message}</p>
          )}

          <button
            type="submit"
            className="landing-cta landing-cta--primary landing-form__submit"
            disabled={state.kind === 'submitting'}
          >
            {state.kind === 'submitting' ? 'Sending…' : 'Send inquiry'}
          </button>
        </form>
      </div>
    </section>
  )
}
